import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/axiosClient';
import { ScanText, CheckCircle2, X, Loader2 } from 'lucide-react';

interface OcrStatusResponse {
  is_active: boolean;
  active_count: number;
  current?: {
    volume_id: number;
    volume_name: string;
    ocr_status: 'processing' | 'queued';
    master_id: number;
    master_title?: string;
    processed_pages: number;
    total_pages: number;
    progress_percent: number;
  } | null;
  recently_completed?: {
    volume_id: number;
    volume_name: string;
    master_id: number;
    master_title?: string;
    completed_at: string;
  } | null;
}

export function OcrGlobalStatus() {
  const navigate = useNavigate();
  const [completedVolume, setCompletedVolume] = useState<{
    volume_id: number;
    volume_name: string;
    master_id: number;
  } | null>(null);

  const prevActiveRef = useRef<boolean>(false);
  const lastActiveVolumeRef = useRef<{ volume_id: number; volume_name: string; master_id: number } | null>(null);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissedVolumeIdsRef = useRef<Set<number>>(new Set());

  const { data: ocrStatus } = useQuery<OcrStatusResponse>({
    queryKey: ['digitization', 'ocr-status'],
    queryFn: async () => {
      const { data } = await apiClient.get('/digitization/ocr-status');
      return data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      // When OCR is active, poll quickly every 2.5s for live updates
      if (data?.is_active) return 2500;
      // When showing completed state, poll every 5s
      if (completedVolume) return 5000;
      // When idle, poll every 7s to catch newly queued jobs
      return 7000;
    },
    staleTime: 2000,
  });

  const isActive = Boolean(ocrStatus?.is_active);
  const currentVolume = ocrStatus?.current;

  // Track the most recently active volume info
  useEffect(() => {
    if (currentVolume) {
      lastActiveVolumeRef.current = {
        volume_id: currentVolume.volume_id,
        volume_name: currentVolume.volume_name,
        master_id: currentVolume.master_id,
      };
      // When a new job is active, clear completion badge
      setCompletedVolume(null);
    }
  }, [currentVolume]);

  // Handle completion transition and recently completed state across page navigation
  useEffect(() => {
    // Case 1: Live transition from active -> inactive
    if (prevActiveRef.current && !isActive) {
      const finished = lastActiveVolumeRef.current || (ocrStatus?.recently_completed ? {
        volume_id: ocrStatus.recently_completed.volume_id,
        volume_name: ocrStatus.recently_completed.volume_name,
        master_id: ocrStatus.recently_completed.master_id,
      } : null);

      if (finished && !dismissedVolumeIdsRef.current.has(finished.volume_id)) {
        setCompletedVolume(finished);

        if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = setTimeout(() => {
          setCompletedVolume(null);
        }, 15000);
      }
    } 
    // Case 2: Fresh route navigation or page load while completed within last 15s
    else if (!isActive && ocrStatus?.recently_completed && !completedVolume) {
      const completedInfo = ocrStatus.recently_completed;
      if (!dismissedVolumeIdsRef.current.has(completedInfo.volume_id)) {
        const completedTime = new Date(completedInfo.completed_at).getTime();
        const elapsedMs = Date.now() - completedTime;
        const remainingMs = 15000 - elapsedMs;

        if (remainingMs > 1000) {
          setCompletedVolume({
            volume_id: completedInfo.volume_id,
            volume_name: completedInfo.volume_name,
            master_id: completedInfo.master_id,
          });

          if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
          dismissTimeoutRef.current = setTimeout(() => {
            setCompletedVolume(null);
          }, remainingMs);
        }
      }
    }

    prevActiveRef.current = isActive;
  }, [isActive, ocrStatus?.recently_completed, completedVolume]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
    };
  }, []);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (completedVolume) {
      dismissedVolumeIdsRef.current.add(completedVolume.volume_id);
    }
    setCompletedVolume(null);
  };

  // 1. RUNNING STATE (Active Processing or Queued)
  if (isActive && currentVolume) {
    const isQueued = currentVolume.ocr_status === 'queued';
    const isProcessing = currentVolume.ocr_status === 'processing';

    return (
      <div
        onClick={() => currentVolume.master_id && navigate(`/digitization/master/${currentVolume.master_id}`)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500/10 via-blue-500/10 to-cyan-500/10 border border-blue-400/40 hover:border-blue-500/70 text-blue-900 shadow-sm hover:shadow transition-all cursor-pointer select-none shrink-0 group animate-in fade-in duration-300"
        title={`OCR is ${isQueued ? 'queued' : 'processing'}: ${currentVolume.volume_name}. Click to view master record.`}
      >
        {/* Pulsing radar dot */}
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600"></span>
        </span>

        {/* Animated scanner / spinner icon */}
        {isProcessing ? (
          <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin shrink-0" />
        ) : (
          <ScanText className="w-3.5 h-3.5 text-blue-600 animate-pulse shrink-0" />
        )}

        {/* Text Details */}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-950 leading-none">
          <span>{isQueued ? 'OCR Queued' : 'OCR Running'}</span>
          {currentVolume.volume_name && (
            <span className="text-[11px] font-normal text-blue-700 max-w-[120px] md:max-w-[160px] truncate hidden sm:inline">
              · {currentVolume.volume_name}
            </span>
          )}
          {currentVolume.total_pages > 0 && currentVolume.processed_pages > 0 && (
            <span className="text-[10px] font-bold text-blue-700 bg-blue-100/90 border border-blue-200/80 px-1.5 py-0.5 rounded-full ml-0.5">
              {currentVolume.processed_pages}/{currentVolume.total_pages}
            </span>
          )}
        </div>
      </div>
    );
  }

  // 2. COMPLETED "TIK" STATE (Shows for 15s after OCR ends)
  if (completedVolume) {
    return (
      <div
        onClick={() => completedVolume.master_id && navigate(`/digitization/master/${completedVolume.master_id}`)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-green-500/15 border border-emerald-400/70 hover:border-emerald-500 text-emerald-900 shadow-sm hover:shadow transition-all cursor-pointer select-none shrink-0 group animate-in zoom-in-95 duration-300"
        title={`OCR completed successfully for ${completedVolume.volume_name}. Click to view.`}
      >
        {/* Animated Check / Tik Icon */}
        <div className="relative flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 transition-transform group-hover:scale-110" />
        </div>

        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-950 leading-none">
          <span className="text-emerald-800 font-bold">OCR Done</span>
          {completedVolume.volume_name && (
            <span className="text-[11px] font-normal text-emerald-700 max-w-[120px] md:max-w-[160px] truncate hidden sm:inline">
              · {completedVolume.volume_name}
            </span>
          )}
        </div>

        {/* Quick dismiss button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="text-emerald-500 hover:text-emerald-800 ml-0.5 p-0.5 rounded-full hover:bg-emerald-200/60 transition-colors"
          title="Dismiss status"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return null;
}
